import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Send, Sparkles, Volume2, Square, Loader2 } from "lucide-react";
import { api, USER_ID, money, orderHandoff } from "../api";
import { VegDot } from "../components";
import { ModuleGate } from "../flags";
import { useSound } from "../sound";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK = ["I want spicy chicken", "Something creamy and mild", "I've never tried Punjabi food", "Vegetarian, not creamy", "ਮੈਨੂੰ ਤਿੱਖਾ ਖਾਣਾ ਚਾਹੀਦਾ ਹੈ", "कुछ मीठा चाहिए"];

const RecCard = ({ dish }) => (
  <div data-testid={`veergi-rec-${dish.slug}`} className="rounded-2xl bg-[#121212] border border-[#D4AF37]/25 overflow-hidden mb-3">
    <div className="flex gap-3 p-3">
      <img src={dish.image} alt={dish.name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <VegDot veg={dish.vegetarian} />
          <p className="text-sm font-bold leading-tight">{dish.name}</p>
        </div>
        <p className="text-[#D4AF37] text-sm font-bold">{money(dish.price)}</p>
        <p className="text-[11px] text-white/60 mt-1 italic">"{dish.reason}"</p>
      </div>
    </div>
    <div className="flex border-t border-white/10">
      <Link data-testid={`veergi-view-${dish.slug}`} to={`/experience/dish/${dish.slug}`} className="flex-1 text-center py-2.5 text-xs font-bold text-white/80 hover:text-white">VIEW DISH</Link>
      <button data-testid={`veergi-order-${dish.slug}`} onClick={() => orderHandoff(dish.order_url, "veergi")} className="flex-1 text-center py-2.5 text-xs font-bold text-[#D4AF37] border-l border-white/10">ADD / ORDER</button>
    </div>
  </div>
);

export default function AskVeerGi() {
  return (
    <ModuleGate flag="ask_veergi">
      <VeerGiChat />
    </ModuleGate>
  );
}

function VeerGiChat() {
  const { soundOn } = useSound();
  const [messages, setMessages] = useState([
    { role: "veergi", text: "Sat Sri Akal! 🙏 I'm VeerGi — your Punjabi food guide. Tell me what you're craving, in English, हिंदी or ਪੰਜਾਬੀ." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `veergi-${Date.now()}`);
  const bottomRef = useRef(null);
  const audioRef = useRef(null);
  const [voiceState, setVoiceState] = useState({});

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setVoiceState((v) => Object.fromEntries(Object.entries(v).map(([k, s]) => [k, s === "playing" ? "idle" : s])));
  };

  useEffect(() => () => stopAudio(), []);

  const speak = async (i, m) => {
    if (voiceState[i] === "playing") return stopAudio();
    stopAudio();
    setVoiceState((v) => ({ ...v, [i]: "loading" }));
    try {
      const r = await api.post("/veergi/speak", {
        text: m.text,
        recommendations: (m.recs || []).map((d) => d.name),
        user_id: USER_ID,
      });
      if (!r.data.audio_url) {
        setVoiceState((v) => ({ ...v, [i]: "idle" }));
        return;
      }
      const audio = new Audio(`${BACKEND_URL}${r.data.audio_url}`);
      audioRef.current = audio;
      audio.onended = () => setVoiceState((v) => ({ ...v, [i]: "idle" }));
      await audio.play();
      setVoiceState((v) => ({ ...v, [i]: "playing" }));
    } catch {
      setVoiceState((v) => ({ ...v, [i]: "idle" }));
    }
  };

  useEffect(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const r = await api.post("/veergi", { message: msg, user_id: USER_ID, session_id: sessionId });
      setMessages((m) => [...m, { role: "veergi", text: r.data.reply, recs: r.data.recommendations }]);
    } catch {
      setMessages((m) => [...m, { role: "veergi", text: "VeerGi stepped away from the tandoor for a second — try again." }]);
    }
    setLoading(false);
  };

  return (
    <div data-testid="ask-veergi-page" className="flex flex-col min-h-[calc(100vh-120px)]">
      <div className="px-4 pt-6 pb-3">
        <div className="flex items-center gap-2 text-[#D4AF37]"><Sparkles size={16} /><span className="text-[10px] tracking-[0.3em]">ASK VEERGI</span></div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Your Punjabi food guide</h1>
      </div>
      <div className="flex-1 px-4 pb-4 overflow-y-auto">
        {messages.map((m, i) => (
          <div key={i} className={`mb-3 ${m.role === "user" ? "flex justify-end" : ""}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-[#2A2A2A] text-white" : "bg-[#1A1A1A] border border-[#D4AF37]/20 text-white/90"}`}>
              {m.text}
              {m.role === "veergi" && soundOn && (
                <button
                  data-testid={`veergi-voice-btn-${i}`}
                  onClick={() => speak(i, m)}
                  className="mt-2 flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[#D4AF37] hover:text-[#F1C40F]"
                >
                  {voiceState[i] === "loading" ? <Loader2 size={13} className="animate-spin" /> : voiceState[i] === "playing" ? <Square size={12} /> : <Volume2 size={13} />}
                  {voiceState[i] === "loading" ? "VEERGI BOL RAHE NE…" : voiceState[i] === "playing" ? "STOP" : "ਸੁਣੋ · HEAR IT IN PUNJABI"}
                </button>
              )}
            </div>
            {m.recs && (
              <div className="mt-3">
                {m.recs.map((d) => <RecCard key={d.slug} dish={d} />)}
                {m.recs.length > 0 && (
                  <button data-testid="veergi-show-another" onClick={() => send("Show me another option")} className="text-xs text-[#D4AF37] underline underline-offset-4">SHOW ME ANOTHER</button>
                )}
              </div>
            )}
          </div>
        ))}
        {loading && <div className="bg-[#1A1A1A] border border-[#D4AF37]/20 rounded-2xl px-4 py-3 text-sm text-white/50 w-fit animate-pulse">VeerGi is thinking…</div>}
        <div ref={bottomRef} />
      </div>
      <div className="px-4 pb-2 flex gap-2 overflow-x-auto hide-scrollbar">
        {QUICK.map((q) => (
          <button key={q} data-testid="veergi-quick-chip" onClick={() => send(q)} className="shrink-0 rounded-full bg-[#1A1A1A] border border-white/10 px-3 py-2 text-[11px] text-white/70 hover:border-[#D4AF37]/40">
            {q}
          </button>
        ))}
      </div>
      <div className="px-4 pb-4 pt-2 sticky bottom-[72px] bg-[#050505]">
        <div className="flex gap-2">
          <input
            data-testid="veergi-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="I want spicy chicken…"
            className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-full px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/50"
          />
          <button data-testid="veergi-send-btn" onClick={() => send()} aria-label="Send" className="bg-[#D4AF37] text-black rounded-full w-12 h-12 flex items-center justify-center hover:bg-[#F1C40F]">
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
